import { capi, eplApi, projectApi } from '@atheneum/api'
import { isCancellationError, showDialog } from '@components/Dialog'
import EPLModel from '@models/EPL.model'
import concat from 'lodash/concat'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import sortBy from 'lodash/sortBy'
import { action, computed, extendObservable } from 'mobx'
import { v4 as generateUUID } from 'uuid'

const defaultFilterGroup = 'segments'
const DEBOUNCE_DELAY_IN_MS = 300

class EPLStore {
  constructor(staticData, growlStore) {
    extendObservable(this, {
      eplModels: [],
      staticData,
      setStatus: false,
      growlStore,
      eplStatus: null,
      eplSingle: null,
      loading: false,
      partialLoad: false,
      error: null,
      doShowGroupsAndFilters: true,
      doShowBatchActions: false,
      projectId: null,
      loadingFilters: false,
      loadingUUID: null,
      recordings: [],
      groupedEplModels: computed(this.groupEpls),
      nonInterviewedStatusComments: computed(
        this.getNonInterviewedStatusComments
      ),
      groupBy: computed(() => {
        if (!this.filterGroups) return defaultFilterGroup
        return Object.values(this.filterGroups).find(
          filterGroup => filterGroup.checked
        ).name
      }),
      filters: null,
      filterGroups: null,
      isExpertQuickSearchActive: false,
      expertQuickSearchValue: '',
      expertQuickSearchPreviousUrl: null
    })
  }

  init() {
    if (window.location.search.includes('unresolved')) {
      this.setStatus = true
    }
    return this.getEplStatus()
  }

  setVisible = pos => {
    for (let epl of this.eplModels) {
      if (this.eplModels.length < 50) {
        epl.isVisible = true
      } else if (!epl.component) {
        epl.isVisible = true
      } else {
        epl.isVisible =
          epl.component.offsetTop > pos - 1000 &&
          epl.component.offsetTop < pos + 2000
      }
    }
  }

  resetStore() {
    this.eplModels = []
    this.showGroupsAndFilters()
    this.filters = null
  }

  showGroupsAndFilters = action(() => {
    this.doShowGroupsAndFilters = true
    this.doShowBatchActions = false
  })

  showBatchActions = action(() => {
    this.doShowBatchActions = true
    this.doShowGroupsAndFilters = false
  })

  groupEpls = () => {
    const actions = {
      eplStatuses: this.groupByStatuses,
      replyStatuses: this.groupByReplyStatuses,
      deliveredBy: this.groupByDeliveredBy,
      segments: this.groupBySegments
    }

    return actions[this.groupBy]()
  }

  getStatusPossibilities = (list, key) => {
    let possibleStatuses = []
    this.eplModels.forEach(epl => {
      const status = this.staticData[list].find(
        status => status.id === epl.epl[key]
      ).id
      if (!possibleStatuses.includes(status) && status !== undefined) {
        possibleStatuses.push(status)
      }
    })

    return possibleStatuses
  }

  groupBySegments = () => {
    let groupedEpls = []

    if (!this.filterGroups) return null
    const segments = this.filterGroups.segments.options
    if (segments.length < 1) return null

    segments.forEach(segment => {
      const epls = this.eplModels.filter(
        epl => get(epl, 'epl.segment.id') === segment.id
      )

      if (!epls || !segment) return null

      groupedEpls.push({
        key: segment.id,
        groupBy: segment.name,
        segment,
        epls
      })
    })

    return groupedEpls
  }

  groupByStatuses = () => {
    let groupedEpls = []
    const statuses = this.getStatusPossibilities('eplStatuses', 'eplStatusId')

    statuses.forEach(statusId => {
      const epls = this.eplModels.filter(
        epl => epl.epl.eplStatusId === statusId
      )
      const eplStatus = this.staticData.eplStatuses.find(
        status => status.id === statusId
      )

      groupedEpls.push({
        key: eplStatus.id,
        groupBy: eplStatus.name,
        epls: epls
      })
    })

    return groupedEpls
  }

  groupByReplyStatuses = () => {
    let groupedEpls = []
    const statuses = this.getStatusPossibilities(
      'replyStatuses',
      'replyStatusId'
    )

    statuses.forEach(statusId => {
      const epls = this.eplModels.filter(
        epl => epl.epl.replyStatusId === statusId
      )
      const replyStatus = this.staticData.replyStatuses.find(
        status => status.id === statusId
      )

      groupedEpls.push({
        key: replyStatus.id,
        groupBy: replyStatus.name,
        epls: epls
      })
    })

    return groupedEpls
  }

  groupByDeliveredBy = () => {
    let deliveredByList = []
    this.eplModels.forEach(epl => {
      let value

      if (!epl.epl.employees.length) value = null
      else value = epl.epl.employees[0].id

      if (!deliveredByList.includes(value) && value !== undefined) {
        deliveredByList.push(value)
      }
    })

    return deliveredByList.map(id => {
      if (id) {
        const epls = this.eplModels.filter(
          epl => epl.epl.employees.length && epl.epl.employees[0].id === id
        )

        return {
          key: id,
          groupBy: get(epls[0], 'epl.employees[0].fullName'),
          epls: epls
        }
      } else {
        const epls = this.eplModels.filter(epl => !epl.epl.employees.length)

        return {
          key: id,
          groupBy: 'Not delivered',
          epls: epls
        }
      }
    })
  }

  groupEplModels() {
    return sortBy(this.eplModels, 'eplGroupIndex')
  }

  getFilterOptions = action(projectId => {
    this.loadingFilters = true

    return projectApi
      .getPipelineFilters(projectId)
      .then(filters => {
        this.filters = filters
        this.initFilterGroups(this.filters)
      })
      .catch(err => {
        throw err
      })
      .then(() => {
        this.loadingFilters = false
      })
  })

  /**
   * Creates a structure to manipulate Grouping and Filtering.
   * Basically extends the options with checked and disabled properties.
   */
  initFilterGroups = filters => {
    if (!filters) return null

    const filterKeys = Object.keys(this.filters)

    this.filterGroups = filterKeys.reduce((filterGroups, filterGroup) => {
      const filterOptions = this.filters[filterGroup]
      const isOptionsListEmpty = filterOptions.length < 1
      const options = filterOptions.map(option => {
        return {
          id: option.id,
          name: option.name,
          checked: false
        }
      })

      filterGroups[filterGroup] = {
        name: filterGroup,
        checked: filterGroup === defaultFilterGroup,
        disabled: isOptionsListEmpty,
        options: options
      }

      return filterGroups
    }, {})
  }

  getEplStatus() {
    return eplApi
      .getEplStatuses()
      .then(eplStatuses => {
        this.eplStatuses = eplStatuses
      })
      .catch(err => {
        this.error = err
      })
  }

  async getSingleEPL(projectId, eplId) {
    this.loading = true
    try {
      const [epl] = await projectApi.getSinglePipelineEpl(projectId, eplId)
      if (epl) {
        let capiProject, capiCall
        try {
          capiProject = (await capi.getProject(projectId))?.project
          if (capiProject) {
            const capiCalls = await capi.getCallsByEplIds([epl.id])
            capiCall = capiCalls[0]
          }
        } catch (e) {
          console.error(e)
        }
        this.eplSingle = new EPLModel(
          epl,
          this.staticData,
          this.growlStore,
          this,
          capiProject,
          capiCall
        )
        this.eplModels = [this.eplSingle]
      } else {
        this.eplSingle = null
      }
      if (this.eplSingle) {
        this.eplSingle.isVisible = true
        this.projectId = projectId // as in getList method
      }
    }
    catch (err) {
      this.error = err
      console.error(err)
    }
    finally {
      this.loading = false
    }
  }

  getMultipleExperts = eplModels => {
    const scheduledExperts = [...eplModels].filter(
      model => model !== undefined && model.epl.eplStatusId === 8
    )

    const schedules = [
      ...new Set(
        scheduledExperts.map(model => {
          const nocancelled = model.epl.expertAvailabilityToEpls.filter(av => {
            return !!av && !!av.schedule && !av.schedule.canceled
          })
          return nocancelled
        })
      )
    ]
      .filter(el => !!el.length)
      .map(element =>
        !element
          ? null
          : element.reduce((earliest, value) => {
            if (
              new Date(value.startTime).getTime() <
              new Date(earliest.startTime).getTime()
            ) {
              return value
            }
            return earliest
          })
      )
      .map(schedule => schedule.startTime)

    const startTimes = [...new Set(schedules)]

    const result = Promise.all(
      startTimes.map(time => projectApi.getZoomMeetings(this.projectId, time))
    )

    return result
  }

  getList(projectId) {
    if (this.loading) return
    this.loading = true

    // TODO: find a better pattern to reuse stores
    if (projectId) {
      this.projectId = projectId
    }

    let filterData

    try {
      if (this.isExpertQuickSearchActive) {
        filterData = { quickSearchQuery: this.expertQuickSearchValue }
      } else {
        filterData = Object.values(this.filterGroups).reduce(
          (accFilterData, filterGroup) => {
            const options = filterGroup.options.reduce((accOptions, option) => {
              if (option.checked) return concat(accOptions, option.id)
              return accOptions
            }, [])
            if (options.length) accFilterData[filterGroup.name] = options
            if (options.length) {
              accFilterData[filterGroup.name] = options
              return accFilterData
            }
            return accFilterData
          },
          {}
        )
      }
    } catch (err) {
      this.loading = false
      console.error(err)
    }

    return this.fetchData(filterData).catch(err => {
      this.loading = false
    })
  }

  async fetchData(filterData, offset = 0, loadingUUID, capiProject) {

    if (!capiProject && this.projectId && offset === 0) {
      try {
        capiProject = (await capi.getProject(this.projectId))?.project
      } catch (e) {
        console.error(e)
      }
    }

    if (!loadingUUID) {
      loadingUUID = generateUUID()
      this.loadingUUID = loadingUUID
    }

    if (this.loadingUUID !== loadingUUID) return

    this.partialLoad = true
    const LIMIT = 25
    try {
      let epls = await projectApi.getPipelineEpls(this.projectId, {
        filters: filterData,
        limit: LIMIT,
        offset
      })
      epls = sortBy(epls, 'eplNumber')
      if (this.loadingUUID !== loadingUUID) return

      let capiCalls = []
      if (capiProject) {
        const eplIds = epls.map(e => e.id)
        if (eplIds.length > 0) {
          capiCalls = await capi.getCallsByEplIds(eplIds)
        }
      }
      const newEpls = epls.map(
        epl => new EPLModel(epl, this.staticData, this.growlStore, this, capiProject, capiCalls.find(c => c.platform_epl_id === epl.id))
      )
      if (offset) {
        this.eplModels = this.eplModels.concat(newEpls)
      } else {
        this.eplModels.replace(newEpls)
      }
      this.setVisible(0)
      this.loading = false

      if (this.setStatus) {
        this.setStatus = false
        this.changeOption('eplStatuses', 8)
      }

      if (epls.length) {
        return this.fetchData(filterData, LIMIT + offset, loadingUUID, capiProject)
      } else {
        this.partialLoad = false
      }
      return Promise.resolve(this.eplModels)

    } catch (err) {
      console.error(err)
      this.partialLoad = false
      this.loading = false
      this.error = err
      throw err
    }
  }

  delete(epl) {
    return showDialog({
      title: 'Delete this expert from pipeline?',
      text: `${epl.expert.title} ${epl.expert.fullName}`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    })
      .then(() => {
        return eplApi.remove(epl.id).then(async res => {
          const capiProject = (await capi.getProject(epl.projectId))?.project
          if (capiProject) {
            const capiCalls = await capi.getCallsByEplIds([epl.id])
            if (capiCalls[0]) {
              try {
                await capi.cancelCall(epl.id, true)
                this.growlStore.showSuccess('Successfully canceled call')
              } catch (error) {
                this.growlStore.showError(error)
              }
            }
          }

          this.getList(epl.projectId)

          this.error = null
          this.growlStore.addNotification({
            title: 'Success!',
            message: 'Expert is removed from the project',
            type: 'success',
            autoClose: true
          })
        })
      })
      .catch(err => {
        if (isCancellationError(err)) return
        this.growlStore.showError(err)
      })
  }

  getNonInterviewedStatusComments = () => {
    const { NOT_INTERVIEWED } = this.staticData.CONST.EPL_STATUSES

    if (!this.eplStatuses) return []

    const eplStatusToEplStatusComments =
      this.eplStatuses.find(s => s.id === NOT_INTERVIEWED)
        .eplStatusToEplStatusComments || []
    const eplStatusComments = eplStatusToEplStatusComments.map(
      entry => entry.eplStatusComment
    )
    return eplStatusComments
  }

  changeOption = action((filterName, id) => {
    const groupOptions = this.filterGroups[filterName].options
    const option = groupOptions.find(option => option.id === id)

    option.checked = !option.checked
    return this.getList()
  })

  quickSearch = debounce(() => {
    this.getList(this.projectId)
  }, DEBOUNCE_DELAY_IN_MS)

  changeFilter = action((filterName, optionId) => {
    const filterGroups = this.filterGroups
    if (filterGroups[filterName].disabled) return null

    Object.values(filterGroups).forEach(filter => {
      filter.checked = filter.name === filterName
    })

    this.getList(this.projectId)
  })

  getRecordingsFromEplHash = async eplHash => {
    this.recordings.replace(await eplApi.getRecordingsFromEplHash(eplHash))
  }

  // #TODO: SharedComponents Reuse - client-interface/utility/epl.js
  generateRecordingDownloadUrl = recording => {
    const zoomRecordingExportUrl = `${window.location.origin}/api/recording/export`

    return `${zoomRecordingExportUrl}/${recording.recordingId}`
  }

  generateRecordingStreamUrl = (epl, recording) => {
    if (recording.fileType === 'TRANSCRIPT') return this.generateRecordingDownloadUrl(recording)

    const hash = recording.fileExtension === 'M4A' ? '#audio' : '#video'
    return `${window.location.origin}/zoom-recording/${epl.hash}/${recording.recordingId}${hash}`
  }

  convertToUSD = async ({ amount, currencyId }) => {
    const usdCurrencyId = 2
    const convertedFee = await eplApi.convertCurrency({
      amount,
      fromCurrencyId: currencyId,
      toCurrencyId: usdCurrencyId
    })

    return convertedFee
  }
}

export default EPLStore
